import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { CommunityMember } from '../../services/communityService';

function avatarColor(id: string): string {
  const colors = ['#d32f2f', '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#0288d1', '#455a64'];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

interface Props {
  open: boolean;
  communityName: string;
  members: CommunityMember[];
  actorClientId: string;
  loading?: boolean;
  onConfirm: (newOwnerClientId: string) => void;
  onClose: () => void;
}

export const TransferOwnershipModal: React.FC<Props> = ({
  open,
  communityName,
  members,
  actorClientId,
  loading,
  onConfirm,
  onClose,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const candidates = members.filter(
    (m) => m.client_id !== actorClientId && m.status === 'active' && m.role !== 'owner'
  );

  useEffect(() => {
    if (!open) setSelectedId(null);
  }, [open]);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Transfer ownership</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: '#5f6368', mb: 2 }}>
          Choose a member to become the new owner of &ldquo;{communityName}&rdquo;. You will become
          a regular member and can leave afterward.
        </Typography>
        {candidates.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#9aa0a6', py: 2, textAlign: 'center' }}>
            No eligible members to transfer to.
          </Typography>
        ) : (
          <List disablePadding>
            {candidates.map((member) => {
              const name = member.display_name || member.client_id;
              return (
                <ListItemButton
                  key={member.client_id}
                  selected={selectedId === member.client_id}
                  onClick={() => setSelectedId(member.client_id)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={member.avatar_url}
                      sx={{ bgcolor: avatarColor(member.client_id) }}
                    >
                      {getInitials(name)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={name} secondary={member.role === 'moderator' ? 'Admin' : 'Member'} />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={() => selectedId && onConfirm(selectedId)}
          variant="contained"
          disabled={!selectedId || loading || candidates.length === 0}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Transfer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
